import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

/* ──────────────────────────────────────────────────────────
 * BACKGROUND — Graph-paper grid + two static radial gradients
 * Replaces generic floating dot particles with something
 * deliberately tied to the systems/engineering identity.
 * A graph-paper grid is used in engineering, architecture,
 * and circuit design — it belongs here.
 * ────────────────────────────────────────────────────────── */

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -2;
  overflow: hidden;
  pointer-events: none;
  contain: strict;
`;

/* Graph-paper grid — very subtle, 1px lines at low opacity */
const GridLayer = styled.div`
  position: absolute;
  inset: 0;

  /* Fine grid (minor lines) */
  background-image:
    linear-gradient(rgba(198, 160, 246, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(198, 160, 246, 0.035) 1px, transparent 1px),
    /* Major grid lines every 5 cells */
    linear-gradient(rgba(198, 160, 246, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(198, 160, 246, 0.06) 1px, transparent 1px);
  background-size:
    20px 20px,
    20px 20px,
    100px 100px,
    100px 100px;
  background-position: center center;

  /* Fade out near the center so it doesn't compete with content */
  mask-image: radial-gradient(
    ellipse 80% 80% at 50% 50%,
    transparent 0%,
    transparent 20%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.6) 80%,
    rgba(0, 0, 0, 0.9) 100%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 80% 80% at 50% 50%,
    transparent 0%,
    transparent 20%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.6) 80%,
    rgba(0, 0, 0, 0.9) 100%
  );

  .light & {
    background-image:
      linear-gradient(rgba(136, 57, 239, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(136, 57, 239, 0.04) 1px, transparent 1px),
      linear-gradient(rgba(136, 57, 239, 0.07) 1px, transparent 1px),
      linear-gradient(90deg, rgba(136, 57, 239, 0.07) 1px, transparent 1px);
  }
`;

/* Two static radial gradients — mauve top-left, teal bottom-right */
/* No animation = no performance cost, no visual noise */
const GradientLayer = styled.div`
  position: absolute;
  inset: 0;

  &::before {
    content: '';
    position: absolute;
    top: -15%;
    left: -10%;
    width: 55vw;
    height: 55vw;
    background: radial-gradient(
      circle at center,
      rgba(198, 160, 246, 0.09) 0%,
      rgba(198, 160, 246, 0.04) 40%,
      transparent 70%
    );
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -15%;
    right: -10%;
    width: 55vw;
    height: 55vw;
    background: radial-gradient(
      circle at center,
      rgba(139, 213, 202, 0.08) 0%,
      rgba(139, 213, 202, 0.03) 40%,
      transparent 70%
    );
    border-radius: 50%;
  }

  .light &::before {
    background: radial-gradient(
      circle at center,
      rgba(136, 57, 239, 0.07) 0%,
      rgba(136, 57, 239, 0.03) 40%,
      transparent 70%
    );
  }

  .light &::after {
    background: radial-gradient(
      circle at center,
      rgba(23, 146, 153, 0.07) 0%,
      rgba(23, 146, 153, 0.03) 40%,
      transparent 70%
    );
  }

  @media (prefers-reduced-motion: reduce) {
    /* Already static — nothing to disable */
  }
`;

export function Background() {
  // Defer decorative layer until after LCP window
  const [showGradients, setShowGradients] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setShowGradients(true), 1200);
    return () => clearTimeout(id);
  }, []);

  return (
    <Container>
      {showGradients && <GradientLayer />}
      <GridLayer />
    </Container>
  );
}
